// Reusable searchable Customer dropdown backed by Customer Master.
//
// Wherever the app used to ask the user to type a customer name freehand
// (Part Master, Item Master, Sales Register PO, Operations invoice…),
// this component replaces the plain <Input> so the value MUST originate
// from Customer Master. Users can still type — matching customers filter
// live; if no exact match exists we surface a `+ Add "<typed name>"`
// action that quick-creates the customer in Customer Master (POST
// /api/customer-master) so nothing blocks the workflow.
//
// Props:
//   value       — currently selected customer NAME (string, matches
//                 `parts.customer` semantics we've used for years).
//   onChange    — (name) => void
//   placeholder — optional
//   testId      — root data-testid; internal elements derive from it
//   allowCreate — when true (default), unknown names can be quick-added.
//   disabled    — pass-through
//
// Data lifecycle:
//   * Fetches /api/customer-master once on mount.
//   * Refetches whenever `customers:changed` window event fires (the
//     Customer Master page emits this on add/edit/delete).
//   * A local add via `+ Add "…"` also dispatches `customers:changed`.
//
// Presentation is a bare, single-file combobox — no shadcn Popover — so
// it drops into any parent height cleanly.

import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, CaretDown, X } from "@phosphor-icons/react";
import { API } from "../App";

export default function CustomerSelect({
  value,
  onChange,
  placeholder = "Search / pick customer…",
  testId = "customer-select",
  allowCreate = true,
  disabled = false,
  className = "",
}) {
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const wrapRef = useRef(null);

  // Keep the input in sync when the parent resets `value`.
  useEffect(() => { setQuery(value || ""); }, [value]);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/customer-master`);
      setCustomers(r.data || []);
    } catch { /* silent — parent form may still function */ }
  };
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("customers:changed", h);
    return () => window.removeEventListener("customers:changed", h);
  }, []);

  // Close on outside click.
  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, []);

  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return customers.slice(0, 100);
    return customers.filter(c => (c.name || "").toLowerCase().includes(q)).slice(0, 100);
  }, [customers, query]);

  const exact = customers.some(c => (c.name || "").toLowerCase() === (query || "").trim().toLowerCase());

  const pick = (name) => {
    setQuery(name);
    onChange && onChange(name);
    setOpen(false);
  };

  const quickCreate = async () => {
    const name = (query || "").trim();
    if (!name) return;
    setCreating(true);
    try {
      await axios.post(`${API}/customer-master`, { name });
      toast.success(`Customer "${name}" added`);
      window.dispatchEvent(new Event("customers:changed"));
      pick(name);
    } catch (e) {
      toast.error("Add failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid={testId}>
      <div className="relative">
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => !disabled && setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && filtered.length === 1) { e.preventDefault(); pick(filtered[0].name); }
            if (e.key === "Escape") setOpen(false);
          }}
          disabled={disabled}
          placeholder={placeholder}
          className="w-full min-h-[38px] border border-[#E2E8F0] rounded px-2 pr-16 text-sm bg-white disabled:bg-[#F1F5F9]"
          data-testid={`${testId}-input`}
        />
        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
          {query && !disabled && (
            <button type="button" onClick={() => { setQuery(""); onChange && onChange(""); }}
              className="p-1 rounded hover:bg-[#F1F5F9] text-[#94A3B8]"
              tabIndex={-1} aria-label="Clear customer"
              data-testid={`${testId}-clear`}>
              <X size={12} />
            </button>
          )}
          <button type="button" onClick={() => !disabled && setOpen(v => !v)}
            className="p-1 rounded hover:bg-[#F1F5F9] text-[#64748B]"
            tabIndex={-1} data-testid={`${testId}-toggle`}>
            <CaretDown size={12} />
          </button>
        </div>
      </div>

      {open && !disabled && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded shadow-lg max-h-72 overflow-y-auto"
             data-testid={`${testId}-list`}>
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-xs text-[#94A3B8] italic">
              {customers.length === 0 ? "No customers yet — add one in Customer Master." : "No matches."}
            </div>
          )}
          {filtered.map(c => (
            <button
              key={c.id || c.name}
              type="button"
              onMouseDown={(e) => e.preventDefault()}   // keep focus so onClick fires before blur
              onClick={() => pick(c.name)}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[#F1F5F9] transition-colors ${
                (c.name || "").toLowerCase() === (query || "").trim().toLowerCase() ? "bg-[#F0FDF4] font-semibold" : ""
              }`}
              data-testid={`${testId}-opt-${(c.name || "").toString().replace(/\s+/g, "_")}`}
            >
              <div className="font-bold text-[#1D2951]">{c.name}</div>
              <div className="text-[10px] text-[#64748B] flex items-center gap-2 flex-wrap">
                {c.vendor_code && <span className="font-mono">VC: {c.vendor_code}</span>}
                {c.location && <span>· {c.location}</span>}
                {c.state && <span>· {c.state}</span>}
              </div>
            </button>
          ))}
          {allowCreate && query.trim() && !exact && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={quickCreate}
              disabled={creating}
              className="w-full text-left px-3 py-2 text-xs bg-[#F0FDF4] hover:bg-[#DCFCE7] border-t border-[#BBF7D0] flex items-center gap-2 font-semibold text-[#166534] disabled:opacity-60"
              data-testid={`${testId}-quick-add`}
            >
              <Plus size={12} />
              {creating ? "Adding…" : <>Add <span className="font-mono">"{query.trim()}"</span> to Customer Master</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
